import {
  IDepot,
  ISupplierUserInput,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import React, {useState} from 'react';
import {apiUploadFile} from 'constants/Api';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import {Visibility, VisibilityOff} from '@material-ui/icons';
import {Autocomplete, Skeleton} from '@material-ui/lab';
import {formattedDepotAddress} from 'screens/Dashboard/Depots/DepotTableSetup';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';

const formattedDepotLatLong = (depot: IDepot) =>
  depot.latitude + ',' + depot.longitude;

export function UserForm(props: {
  loggedInUser: ISupplierUserProfile;
  user?: ISupplierUserInput;
  isLoadingDepots?: boolean;
  defaultDepots?: IDepot[];
  depots: IDepot[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (user: ISupplierUserInput) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  shouldCapturePassword?: boolean;
  hideDepotFields?: boolean;
  smallWidth?: boolean;
  readonly?: boolean;
}) {
  type Submission = ISupplierUserInput & {
    confirmPassword: string;
  };

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control, getValues} = useForm<Submission>({
    defaultValues: {
      ...(!!props.defaultDepots?.length && {
        depotIds: props.defaultDepots.map((depot) => depot.id),
        latlong: formattedDepotLatLong(props.defaultDepots[0]),
        address: formattedDepotAddress(props.defaultDepots[0]),
      }),
      ...(!!props.user?.depotIds?.length && {
        depotIds: props.user?.depotIds,
      }),
      brandSupplierId: props.loggedInUser.brand?.id,
      companyId: props.loggedInUser.company?.id,
      isActive: true,
      ...props?.user,
    },
  });

  const [isUploadingProfileImage, setIsUploadingProfileImage] = useState(false);
  const [isProfileImageDragActive, setIsProfileImageDragActive] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDepotSelect, setShowDepotSelect] = useState(!props.user?.isAdmin);

  const getImageFromFileList = (files?: FileList | null) => {
    if (!files?.length) return undefined;
    return Array.from(files).find((file) => file.type.startsWith('image/'));
  };

  const uploadImage = (image?: File) => {
    if (!image) return;

    const fileExtension = image.name.split('.').pop()?.substr(0, 4);
    const fileKey = `${new Date().getTime()}_${
      props.loggedInUser.userId
    }_profile.${fileExtension}`;

    setIsUploadingProfileImage(true);
    return apiUploadFile(image, fileKey)
      .catch((error) => {
        alert('Error uploading image. Please try again later');
        console.error(error);
        throw error;
      })
      .finally(() => setIsUploadingProfileImage(false));
  };

  const handleImageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    uploadImage(getImageFromFileList(event.target.files))?.then(onChange);
    event.target.value = '';
  };

  const handleImageDrop = (
    event: React.DragEvent<HTMLElement>,
    onChange: (value: string) => void,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setIsProfileImageDragActive(false);

    if (props.readonly || isUploadingProfileImage) return;

    const image = getImageFromFileList(event.dataTransfer.files);
    if (!image && event.dataTransfer.files.length) {
      alert('Please drop an image file');
      return;
    }

    uploadImage(image)?.then(onChange);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const findDepotsByIds = (ids?: number[]) => {
    if (!ids?.length) return [];
    return props.depots.filter((depot) => ids.includes(depot.id));
  };

  const onSubmit = (submission: Submission) => {
    // admin default depot
    if (submission.isAdmin) {
      submission.depotIds = props.defaultDepots?.map((depot) => depot.id) || [
        props.depots?.[0]?.id,
      ];
    }
    // @ts-ignore
    delete submission.confirmPassword;
    const firstDepot = props.depots.find(
      (depot) => submission.depotIds![0] == depot.id,
    );
    const user: ISupplierUserInput = {
      id: submission.id,
      name: submission.name,
      surname: submission.surname,
      email: submission.email,
      password: submission.password,
      phone: submission.phone,
      pictureUrl: submission.pictureUrl,
      isAdmin: submission.isAdmin,
      brandSupplierId: submission.brandSupplierId,
      companyId: submission.companyId,
      isActive: submission.isActive,
      depotIds: submission.depotIds,
      latlong: submission.latlong,
      address: submission.address,
      ...(firstDepot && {
        latlong: formattedDepotLatLong(firstDepot),
        address: formattedDepotAddress(firstDepot),
      }),
    };
    props.onSubmit(user);
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="pictureUrl"
            control={control}
            render={({field: {onChange, value}}) => (
              <Box
                className={`photo-upload-container drag-drop-upload form-section${
                  isProfileImageDragActive ? ' is-dragging' : ''
                }`}
                onDragEnter={(event: React.DragEvent<HTMLElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!props.readonly && !isUploadingProfileImage) {
                    setIsProfileImageDragActive(true);
                  }
                }}
                onDragOver={(event: React.DragEvent<HTMLElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onDragLeave={(event: React.DragEvent<HTMLElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsProfileImageDragActive(false);
                }}
                onDrop={(event: React.DragEvent<HTMLElement>) =>
                  handleImageDrop(event, onChange)
                }
              >
                <IconButton
                  component="label"
                  className="avatar"
                  size="medium"
                  disabled={props.readonly || isUploadingProfileImage}
                >
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      handleImageInputChange(event, onChange);
                    }}
                  />
                  <div className="avatar-container">
                    {isUploadingProfileImage ? (
                      <CircularProgress
                        color={'primary'}
                        size={20}
                        thickness={6}
                        className={'avatar'}
                      />
                    ) : (
                      <Avatar src={value} className="avatar avatar-border" />
                    )}
                  </div>
                </IconButton>
                {!props.readonly && (
                  <Button
                    component="label"
                    disabled={isUploadingProfileImage}
                    size="small"
                    fullWidth
                  >
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => {
                        handleImageInputChange(event, onChange);
                      }}
                    />
                    Upload Image
                  </Button>
                )}
                {!props.readonly && (
                  <Typography className="drag-drop-hint">
                    Drag and drop an image here
                  </Typography>
                )}
              </Box>
            )}
          />
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="name"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    autoFocus
                    className="input"
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'First name required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="surname"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Surname"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Surname required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="phone"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Phone"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Phone required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="email"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Email required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Valid email address required',
                  },
                }}
              />
            </Grid>
          </Grid>
          {props.shouldCapturePassword && (
            <Grid container spacing={2} className="form-section">
              <Grid item xs={12}>
                <Typography variant={'subtitle2'}>Password</Typography>
              </Grid>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Controller
                  name="password"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      className="input"
                      label="Password"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={props.readonly || props.isLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              onMouseDown={handleTogglePasswordVisibility}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  rules={{
                    required: 'Password required',
                    minLength: {
                      value: 8,
                      message: 'Password should contain at least 8 characters',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      className="input"
                      label="Confirm password"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={props.readonly || props.isLoading}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              onMouseDown={handleTogglePasswordVisibility}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  rules={{
                    required: 'Password required',
                    validate: (value) =>
                      value === getValues('password') ||
                      'Passwords do not match',
                  }}
                />
              </Grid>
            </Grid>
          )}
          {!props.hideDepotFields && (
            <Grid container spacing={2} className="form-section">
              <Grid item xs={12}>
                <Typography variant={'subtitle2'}>Depot Information</Typography>
              </Grid>
              <Grid item xs={12} style={{marginTop: '-0.5rem'}}>
                <Controller
                  name="isAdmin"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          defaultChecked={props.user?.isAdmin}
                          checked={value}
                          onChange={(event) => {
                            const isAdmin = event.target.checked;
                            onChange(isAdmin);
                            setShowDepotSelect(!isAdmin);
                          }}
                          disabled={props.readonly || props.isLoading}
                        />
                      }
                      label={
                        <Typography variant={'body2'} color={'textSecondary'}>
                          Admin User - Manage all depots
                        </Typography>
                      }
                    />
                  )}
                />
              </Grid>
              {showDepotSelect && (
                <Grid item xs={12}>
                  {props.isLoadingDepots && (
                    <Skeleton variant="rect" height={54} className="input" />
                  )}
                  {!props.isLoadingDepots && (
                    <Controller
                      name="depotIds"
                      control={control}
                      render={({
                        field: {onChange, value},
                        fieldState: {error},
                      }) => (
                        <Autocomplete
                          id="depots"
                          disableClearable
                          options={props.depots}
                          getOptionLabel={(depot: IDepot) => depot.name}
                          size="small"
                          fullWidth
                          disabled={props.readonly || props.isLoading}
                          multiple
                          value={findDepotsByIds(value)}
                          onChange={(event, selectedDepots: IDepot[]) => {
                            const depotIds = selectedDepots.map(
                              (depot) => depot.id,
                            );
                            onChange(depotIds);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              className="input"
                              label="Depots"
                              variant="outlined"
                              fullWidth
                              error={!!error}
                              helperText={error ? error.message : null}
                              disabled={props.readonly || props.isLoading}
                              size="medium"
                            />
                          )}
                        />
                      )}
                      rules={{
                        required: 'Depot required',
                      }}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          )}
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={
                      props.isLoading ||
                      props.isLoadingDepots ||
                      isUploadingProfileImage
                    }
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={
                    props.isLoading ||
                    props.isLoadingDepots ||
                    isUploadingProfileImage
                  }
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
